import type { SVGProps } from 'react';

/**
 * Iconos de línea, inline.
 *
 * Trazo de 1.75 y `currentColor`: heredan el color del contexto y no exigen
 * una librería de iconos ni una petición extra.
 */
function Base(props: Readonly<SVGProps<SVGSVGElement>>) {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      focusable="false"
      height="20"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.75"
      viewBox="0 0 24 24"
      width="20"
      {...props}
    />
  );
}

export type IconName =
  | 'inicio'
  | 'insumos'
  | 'stock'
  | 'menu'
  | 'kardex'
  | 'alertas'
  | 'reportes'
  | 'usuarios'
  | 'salir'
  | 'tendencia'
  | 'dinero'
  | 'caja'
  | 'buscar'
  | 'mas'
  | 'flecha'
  | 'calendario'
  | 'local'
  | 'chevron'
  | 'cerrar'
  | 'papelera'
  | 'descargar'
  | 'carrito'
  | 'idea'
  | 'info'
  | 'personas'
  | 'puntos'
  | 'arrastrar'
  | 'sube'
  | 'baja'
  | 'prohibido'
  | 'listo';

const PATHS: Readonly<Record<IconName, React.ReactNode>> = {
  inicio: <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" />,
  insumos: (
    <>
      <path d="M4 7h16M4 12h16M4 17h10" />
      <circle cx="19" cy="17" r="2" />
    </>
  ),
  stock: (
    <>
      <path d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5z" />
      <path d="M3 7.5 12 12l9-4.5M12 12v9" />
    </>
  ),
  menu: (
    <>
      <path d="M4 20h16" />
      <path d="M5 16a7 7 0 0 1 14 0z" />
      <path d="M12 6V4" />
    </>
  ),
  kardex: (
    <>
      <path d="M5 3h10l4 4v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M15 3v4h4M8 13h8M8 17h5" />
    </>
  ),
  alertas: (
    <>
      <path d="M12 4 2.5 20h19z" />
      <path d="M12 10v4M12 17h.01" />
    </>
  ),
  reportes: (
    <>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </>
  ),
  usuarios: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16 5.5a3.2 3.2 0 0 1 0 5M18 20a6.4 6.4 0 0 0-2-4.6" />
    </>
  ),
  salir: (
    <>
      <path d="M15 4h3a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-3" />
      <path d="M10 16l-4-4 4-4M6 12h9" />
    </>
  ),
  tendencia: (
    <>
      <path d="M3 17l5.5-5.5 3.5 3.5L21 6" />
      <path d="M15 6h6v6" />
    </>
  ),
  dinero: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M14.5 9.5a2.8 2.8 0 0 0-2.5-1.3c-1.5 0-2.5.8-2.5 2s1 1.7 2.5 2 2.5.8 2.5 2-1 2-2.5 2a2.8 2.8 0 0 1-2.5-1.3M12 6.5v11" />
    </>
  ),
  caja: (
    <>
      <path d="M3 8.5 12 4l9 4.5v7L12 20l-9-4.5z" />
      <path d="M7.5 6.3 16.5 11v6" />
    </>
  ),
  buscar: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  mas: <path d="M12 5v14M5 12h14" />,
  flecha: <path d="M5 12h14m-6-6 6 6-6 6" />,
  calendario: (
    <>
      <rect height="16" rx="2" width="18" x="3" y="5" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  local: (
    <>
      <path d="M4 21V9l8-5 8 5v12" />
      <path d="M9 21v-6h6v6" />
    </>
  ),
  chevron: <path d="m6 9 6 6 6-6" />,
  cerrar: <path d="M6 6l12 12M18 6 6 18" />,
  papelera: (
    <>
      <path d="M4 7h16M10 11v6M14 11v6" />
      <path d="M6 7l1 13h10l1-13M9 7V4h6v3" />
    </>
  ),
  descargar: <path d="M12 4v12m0 0 4-4m-4 4-4-4M4 20h16" />,
  carrito: (
    <>
      <circle cx="9" cy="20" r="1.4" />
      <circle cx="18" cy="20" r="1.4" />
      <path d="M3 4h2l2.5 11h11L21 7H6" />
    </>
  ),
  idea: (
    <>
      <path d="M9 18h6M10 21h4" />
      <path d="M12 3a6 6 0 0 0-3.5 10.9c.6.5.9 1.1 1 1.8h5c.1-.7.4-1.3 1-1.8A6 6 0 0 0 12 3Z" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </>
  ),
  personas: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20c0-3.3 2.7-5.4 6-5.4s6 2.1 6 5.4" />
      <path d="M16 5.2a3.2 3.2 0 0 1 0 5.6M17.5 14.9c2 .7 3.5 2.4 3.5 5.1" />
    </>
  ),
  puntos: (
    <>
      <circle cx="12" cy="5" r="1.3" />
      <circle cx="12" cy="12" r="1.3" />
      <circle cx="12" cy="19" r="1.3" />
    </>
  ),
  arrastrar: (
    <>
      <circle cx="9" cy="6" r="1.2" />
      <circle cx="15" cy="6" r="1.2" />
      <circle cx="9" cy="12" r="1.2" />
      <circle cx="15" cy="12" r="1.2" />
      <circle cx="9" cy="18" r="1.2" />
      <circle cx="15" cy="18" r="1.2" />
    </>
  ),
  sube: <path d="M12 19V5m0 0-6 6m6-6 6 6" />,
  baja: <path d="M12 5v14m0 0 6-6m-6 6-6-6" />,
  prohibido: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12h8" />
    </>
  ),
  listo: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12.5 2.5 2.5 4.5-5" />
    </>
  ),
};

export interface IconProps extends SVGProps<SVGSVGElement> {
  readonly name: IconName;
}

export function Icon({ name, ...rest }: Readonly<IconProps>) {
  return <Base {...rest}>{PATHS[name]}</Base>;
}
